// Coverage summary for the Plan board. Buckets every slot so ops can see at a
// glance what still needs attention — especially GAPS (open slots nobody can be
// assigned to: everyone eligible marked ❌, or no eligible guide exists).
//
// Buckets are mutually exclusive, in precedence order:
//   excluded  → operator-excluded from the ✨/❌ picker (PSCT History, FLG Mural, R66 11:00)
//   assigned  → someone is already on it
//   fillable  → open, not excluded, has ≥1 ✨-preferred guide
//   coverable → open, no ✨ yet, but ≥1 unmarked qualified guide could take it
//   gap       → open with NO assignable candidate at all (a real coverage hole)

import type { PlanSlot } from "@/lib/load";
import { candidatesFor } from "@/lib/eligibility";

export interface CoverageSummary {
  total: number;
  assigned: number;
  fillable: number;
  coverable: number;
  gaps: number;
  excluded: number;
  gapSlots: PlanSlot[];
}

/** Assigned by any Notion signal — display names OR the reliable cmau_ id. */
function isAssigned(slot: PlanSlot): boolean {
  return slot.assignedGuideNames.length > 0 || !!slot.assignedGuidePeekId;
}

/** A true coverage hole: open, in scope, and nobody assignable (✨ or open). */
export function isGap(slot: PlanSlot): boolean {
  if (isAssigned(slot) || slot.excluded) return false;
  const c = candidatesFor(slot);
  return c.preferred.length === 0 && c.open.length === 0;
}

export function summarizeSlots(slots: PlanSlot[]): CoverageSummary {
  const s: CoverageSummary = {
    total: slots.length,
    assigned: 0,
    fillable: 0,
    coverable: 0,
    gaps: 0,
    excluded: 0,
    gapSlots: [],
  };
  for (const slot of slots) {
    if (slot.excluded) {
      s.excluded++;
    } else if (isAssigned(slot)) {
      s.assigned++;
    } else {
      const c = candidatesFor(slot);
      if (c.preferred.length > 0) {
        s.fillable++;
      } else if (c.open.length > 0) {
        s.coverable++;
      } else {
        s.gaps++;
        s.gapSlots.push(slot);
      }
    }
  }
  return s;
}
